<script setup lang="ts">
  import { computed, ref, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import {
    PROCEED_EVENT_NAME,
    LOAD_EVENT_NAME,
    ADULTS_COUNT_MIN,
    ADULTS_COUNT_MAX,
    MAX_TRAVELLERS_COUNT,
    CHILDREN_COUNT_MIN,
    CHILDREN_COUNT_MAX,
  } from './FlowTravelersWidget.constants'
  import AmountCounter from '@/entities/Flow/components/AmountCounter/AmountCounter.vue'
  import ContactUsWidget from '@/widgets/ContactUsWidget/ContactUsWidget.vue'
  import { TravelDirection } from '@/shared/types/common.types'
  import { usePerformanceTiming, useGoogleAnalytics } from '@/shared/composables'
  import { useRoute } from 'vue-router'
  import type { InboundFlowRoutes, OutboundFlowRoutes } from '@/shared/types/router.types'

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
  }

  interface Props {
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
  }

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
  })

  const emit = defineEmits<Emits>()
  const route = useRoute()
  const gaService = useGoogleAnalytics()
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { t } = useI18n()
  const { onPerformanceCheckEnd } = usePerformanceTiming()

  const isInbound = props.typeOfTravel === TravelDirection.INBOUND

  const annotationText = isInbound
    ? t('inbound.traveling_persons.description.text')
    : t('outbound.traveling_persons.annotation.text')

  const annotationLink = isInbound
    ? t('inbound.traveling_persons.description.link')
    : t('outbound.traveling_persons.annotation.link')

  const adultsTitle = isInbound
    ? t('inbound.traveling_persons.adults.label')
    : t('outbound.traveling_persons.adults.label')

  const adultsAnnotation = isInbound
    ? t('inbound.traveling_persons.adults.annotation')
    : t('outbound.traveling_persons.adults.annotation')

  const childrenTitle = isInbound
    ? t('inbound.traveling_persons.children.label')
    : t('outbound.traveling_persons.children.label')

  const childrenAnnotation = isInbound
    ? t('inbound.traveling_persons.children.annotation')
    : t('outbound.traveling_persons.children.annotation')

  const adultsCount = ref(userStore.adultsCount)
  const childrenCount = ref(userStore.childrenCount)
  const isContactUsPopupActive = ref(false)
  const totalTravellersCount = computed(() => adultsCount.value + childrenCount.value)
  const isMaxCounterDisabled = computed(() => totalTravellersCount.value >= MAX_TRAVELLERS_COUNT)

  const openContactPopup = () => {
    isContactUsPopupActive.value = true
    gaService.sendContactUsPopup(route.name as InboundFlowRoutes | OutboundFlowRoutes)
  }

  const onProceed = async () => {
    if (totalTravellersCount.value > MAX_TRAVELLERS_COUNT) {
      return
    }

    emit(LOAD_EVENT_NAME, async () => {
      userStore.setTravelersCount(adultsCount.value, childrenCount.value, true)
      await commonStore.sendFormData({
        numAdults: adultsCount.value,
        numChildren: childrenCount.value,
      })

      gaService.sendNumberOfTravelersProceed(
        props.typeOfTravel,
        adultsCount.value,
        childrenCount.value,
        userStore.destination?.map((country) => country?.id || '')
      )

      emit(PROCEED_EVENT_NAME)
    })
  }

  onMounted(() => {
    onPerformanceCheckEnd()
  })
</script>

<template>
  <FlowCard :title="t('inbound_traveling_persons_header')" with-button @button-click="onProceed">
    <ContactUsWidget v-model="isContactUsPopupActive" data-test="travelers-widget" />
    <div class="travelers-widget">
      <div class="travelers-widget__annotation travelers-widget__description">
        <span v-html="annotationText"></span>&nbsp;<button
          data-test="travelers-widget-contact-button"
          @click="openContactPopup"
        >
          {{ annotationLink }}
        </button>
      </div>

      <div>
        <AmountCounter
          v-model="adultsCount"
          :title="adultsTitle"
          :description="adultsAnnotation"
          :min-value="ADULTS_COUNT_MIN"
          :max-value="ADULTS_COUNT_MAX"
          :min-disabled="adultsCount === ADULTS_COUNT_MIN"
          :max-disabled="isMaxCounterDisabled"
          data-test="adults-counter"
        />
        <AmountCounter
          v-model="childrenCount"
          :title="childrenTitle"
          :description="childrenAnnotation"
          :min-value="CHILDREN_COUNT_MIN"
          :max-value="CHILDREN_COUNT_MAX"
          :min-disabled="childrenCount === CHILDREN_COUNT_MIN"
          :max-disabled="isMaxCounterDisabled"
          data-test="children-counter"
        />
      </div>

      <p
        v-if="isMaxCounterDisabled"
        class="travelers-widget__annotation travelers-widget__warning"
        v-html="$t('inbound.traveling_persons.count_warning')"
      ></p>
    </div>
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowTravelersWidget';
</style>
